/**
 * PATH: packages/ui/src/calendar/scheduleOverlay.ts
 * FILE_ID: UI-CALENDAR-SCHEDULE-OVERLAY-001
 * PURPOSE: Contract for the read-only "my scheduled jobs" overlay. The host
 *          injects a fetcher (same pattern as fetchWeather) so the calendar
 *          package stays auth and backend agnostic; the Diary's schedule feed
 *          is the one source both calendars read.
 * INVARIANT: a schedule row is never editable. asScheduleRows re-stamps every
 *          host row (source, no master id, not organiser, not recurring), so
 *          isOccurrenceEditable is false whatever the host sends.
 * INVARIANT: a row's colour is the Schedule planner's colour for its job,
 *          sent by the host on `schedule.colour` (#1256). This package cannot
 *          import the planner, so it only mirrors the planner's neutral, pinned
 *          equal by apps/workforce shiftColour.test.ts.
 */

import type { EventOccurrence, ScheduleColour } from './types'

/** The planner's colour for a booking with no tag and no job
 *  (job-hub getPhaseStyle(null), the 'Other' lane). */
export const SCHEDULE_NEUTRAL_COLOUR: Readonly<ScheduleColour> = Object.freeze({
  bg: '#EDE9FE',
  text: '#3730a3',
  border: '#8b5cf6',
})

/**
 * @deprecated Change #1256: schedule rows wear their job's planner colour
 * (`EventOccurrence.schedule.colour`). Kept one release for callers outside
 * this repo; it is the planner neutral edge now, not the old fixed teal.
 */
export const SCHEDULE_OVERLAY_COLOR = SCHEDULE_NEUTRAL_COLOUR.border

/** The padded window the grid rendered, as ISO instants. */
export interface CalendarScheduleWindow {
  start: string
  end: string
}

export type FetchCalendarSchedule = (window: CalendarScheduleWindow) => Promise<EventOccurrence[]>

/** A host row with the fields every overlay needs (uid, start, end). */
export function isOccurrenceRow(r: unknown): r is EventOccurrence {
  if (!r || typeof r !== 'object') return false
  const o = r as Partial<EventOccurrence>
  return typeof o.uid === 'string' && typeof o.start_at === 'string' && typeof o.end_at === 'string'
}

/** Keep well-formed rows and stamp them read-only schedule rows. */
export function asScheduleRows(rows: unknown): EventOccurrence[] {
  if (!Array.isArray(rows)) return []
  return rows.filter(isOccurrenceRow).map((r) => ({
    ...r,
    source: 'schedule' as const,
    event_id: null,
    calendar_id: null,
    is_organizer: false,
    is_recurring: false,
  }))
}
